/*----------------------------------
- PERSONA PAIR
----------------------------------*/

/**
 * The persona pair's conversion wrapper: the marketing `PersonaPair` part (the investor half in the ink `cta` pill,
 * the founder half in the soft one, both 44px with the go circle; batch 6 S2b) fed with the hrefs and the tracking of
 * the caller's attribution. Each opens sign-up as that persona (`personaSource: 'chosen'`), so the modal's persona
 * screen opens on it, preselected. Rendered by the product feature hero (`.wp-acts.mk-pair`, section
 * `hero`) and the `/domains*` closing block (`.dm-cta__acts`, section `domains_footer`); a rebuilt page that needs the
 * pair renders this one, never a copy (batch 7 part 3 review, M8). The older `PersonaAuthCtaPair` left with its last
 * reader, the agenda's "Your dates" card (batch 4 route 2, part 2).
 *
 * @docs docs/features/website-landing-experience
 * @rule The conversion path is frozen: the hrefs come from `buildLandingPersonaSignupHref` with the caller's
 *       attribution, the clicks fire the caller's catalogued `*_investor` / `*_founder` ids with its section and
 *       surface, the analytics label is `PERSONA_CTA_TRACKING_LABELS` whatever the buttons read, and every link keeps
 *       `persona-auth-cta`, `data-persona` and `data-attribution` (persona-auth-cta-contract).
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import type { ConversionTriggerId } from '@uniquedomains/components/contracts/analytics/conversionTriggers';
import { PersonaPair as PersonaPairPart } from '@uniquedomains/components/marketing/persona-pair/PersonaPair';

import {
    buildLandingPersonaSignupHref,
    PERSONA_CTA_TRACKING_LABELS,
    type LandingSignupPersona,
} from '@/client/catalogs/landing/publicLinks';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { trackLandingCta } from '@/client/pages/landing/lib/trackLandingCta';

/*----------------------------------
- TYPES
----------------------------------*/

type PersonaPairProps = {
    attribution: string;
    investorTriggerId: ConversionTriggerId;
    founderTriggerId: ConversionTriggerId;
    page: string;
    section: string;
    surface: string;
    /** What the two buttons read; the analytics label never follows it. */
    labels: Record<LandingSignupPersona, string>;
    /** The row's placement class on the caller's page (`wp-acts mk-pair`, `dm-cta__acts`). */
    class: string;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const PersonaPair = ({ attribution, investorTriggerId, founderTriggerId, page, section, surface, labels, class: klass }: PersonaPairProps) => {
    const investorHref = buildLandingPersonaSignupHref('investor', attribution);
    const founderHref = buildLandingPersonaSignupHref('founder', attribution);
    const trackEntry = (persona: LandingSignupPersona, href: string, triggerId: ConversionTriggerId) => {
        trackLandingCta({ page, section, label: PERSONA_CTA_TRACKING_LABELS[persona], kind: 'persona', href, surface });
        trackConversionTrigger(triggerId, { href, page, section, context: { persona } });
    };

    return (
        <PersonaPairPart
            class={klass}
            investor={{ label: labels.investor, href: investorHref, attribution, onClick: () => trackEntry('investor', investorHref, investorTriggerId) }}
            founder={{ label: labels.founder, href: founderHref, attribution, onClick: () => trackEntry('founder', founderHref, founderTriggerId) }}
        />
    );
};

export default PersonaPair;
